import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  applyConsent,
  readConsent,
  saveConsent,
  trackPageView,
} from "@/lib/analyticsConsent";

export const OPEN_COOKIE_PREFERENCES_EVENT = "open-cookie-preferences";

export default function CookieConsent() {
  const { pathname } = useLocation();
  const initial = useRef(readConsent()).current;
  const [bannerOpen, setBannerOpen] = useState(!initial);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(initial?.analytics ?? false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (initial) applyConsent(initial.analytics);
  }, [initial]);

  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  useEffect(() => {
    const open = () => {
      returnFocusRef.current = document.activeElement as HTMLElement;
      setAnalytics(readConsent()?.analytics ?? false);
      setBannerOpen(false);
      setSettingsOpen(true);
    };
    window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, open);
  }, []);

  useEffect(() => {
    if (!settingsOpen) return;
    dialogRef.current?.querySelector<HTMLElement>("button, input")?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSettingsOpen(false);
        returnFocusRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [settingsOpen]);

  const choose = (value: boolean) => {
    saveConsent(value);
    setAnalytics(value);
    setBannerOpen(false);
    setSettingsOpen(false);
    if (value) trackPageView(pathname);
    returnFocusRef.current?.focus();
  };

  return (
    <>
      {bannerOpen && (
        <section aria-label="Cookie preferences" className="fixed inset-x-4 bottom-4 z-[100] mx-auto max-w-5xl rounded-2xl border border-[#2B2B2B]/15 bg-[#FBF9F6] p-5 shadow-2xl md:p-6">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <h2 className="font-fraunces text-xl text-[#2B2B2B]">Your privacy choices</h2>
              <p className="mt-2 font-manrope text-sm leading-relaxed text-[#2B2B2B]/75">
                We use necessary browser storage for your preferences. With your permission, we also use Google Analytics and Google Ads measurement to understand general website use and whether ads lead to website activity. Optional measurement is off unless you choose it.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row md:flex-wrap md:justify-end">
              <button type="button" onClick={() => choose(false)} className="rounded-full border border-[#2B2B2B]/30 px-4 py-2 text-sm font-semibold">Reject non-essential</button>
              <button type="button" onClick={() => { returnFocusRef.current = document.activeElement as HTMLElement; setBannerOpen(false); setSettingsOpen(true); }} className="rounded-full border border-[#3C6FF8] px-4 py-2 text-sm font-semibold text-[#3C6FF8]">Manage preferences</button>
              <button type="button" onClick={() => choose(true)} className="rounded-full bg-[#3C6FF8] px-4 py-2 text-sm font-semibold text-white">Accept all</button>
            </div>
          </div>
        </section>
      )}

      {settingsOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/45 p-4" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && readConsent()) setSettingsOpen(false); }}>
          <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="cookie-settings-title" className="w-full max-w-xl rounded-2xl bg-[#FBF9F6] p-6 shadow-2xl md:p-8">
            <h2 id="cookie-settings-title" className="font-fraunces text-2xl text-[#2B2B2B]">Cookie preferences</h2>
            <p className="mt-2 text-sm leading-relaxed text-[#2B2B2B]/70">Choose whether we may use optional analytics. You can change this choice at any time.</p>
            <div className="mt-6 space-y-4">
              <div className="rounded-xl border border-[#2B2B2B]/15 p-4">
                <div className="flex items-center justify-between gap-4"><h3 className="font-semibold">Necessary</h3><span className="text-sm font-semibold text-[#6B742F]">Always on</span></div>
                <p className="mt-1 text-sm text-[#2B2B2B]/70">Supports essential site operation, security, and remembering your privacy choice.</p>
              </div>
              <label className="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-[#2B2B2B]/15 p-4">
                <span><span className="block font-semibold">Analytics & ad measurement</span><span className="mt-1 block text-sm text-[#2B2B2B]/70">Allows Google Analytics and Google Ads measurement to measure aggregate visits, traffic sources, and campaign effectiveness. No form values or clinical information are sent, and ad personalization remains off.</span></span>
                <input type="checkbox" className="mt-1 h-5 w-5 accent-[#3C6FF8]" checked={analytics} onChange={(event) => setAnalytics(event.target.checked)} aria-label="Allow analytics" />
              </label>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => choose(false)} className="rounded-full border border-[#2B2B2B]/30 px-5 py-2.5 text-sm font-semibold">Reject non-essential</button>
              <button type="button" onClick={() => choose(analytics)} className="rounded-full bg-[#3C6FF8] px-5 py-2.5 text-sm font-semibold text-white">Save preferences</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
